import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { PriceInput } from "components/PriceInput/PriceInput";
import { FormikValues } from "formik";
import { DftReviewSchema } from "formValidations/DftReviewSchema";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useModal } from "hooks/useModal";
import { Parsed1120FieldType } from "store/apis/filingDraftReview";
import { useGetPreviewUrlQuery } from "store/apis/previewUrl";
import {
  resetDftDraftForReview,
  setDftDraftForReview,
} from "store/slices/dftFilingDraftReview";
import { ModalProps } from "types/utils/modal";
import { DiscardFilingDraft } from "../DiscardFilingDraft";
import { ParseDftLoader } from "./ParseDftLoader";
import { QuestionWithCircle } from "components/icons/QuestionWithCircle";
import { Link } from "components/DesignSystem/Link/Link";

export type ReviewParsed1120FieldsType = Parsed1120FieldType & {
  isEdited?: boolean;
};

export const ReviewDftFields = ({
  isOpen,
  close,
  isLoading,
  isSuccess,
  taxAmountPayable = "",
  grossAssetValue = "",
  fileId,
  groupId,
}: ModalProps & {
  isLoading: boolean;
  isSuccess: boolean;
  fileId?: string;
  groupId?: string;
  taxAmountPayable?: number | string;
  grossAssetValue?: number | string;
}) => {
  const discardDraft = useModal();
  const dispatch = useAppDispatch();
  const {
    data,
    isLoading: isPreviewLoading,
    isSuccess: isPreviewSuccess,
  } = useGetPreviewUrlQuery(
    {
      groupId: groupId!,
      fileId,
    },
    {
      skip: !Boolean(fileId) || !groupId,
      refetchOnMountOrArgChange: true,
    }
  );
  const fileUrl = data?.preview_url || "";

  const handleSendDraftToCustomer = (values: FormikValues) => {
    if (fileId && groupId) {
      dispatch(
        setDftDraftForReview({
          file: {
            preview_url: data?.preview_url || "",
            download_url: data?.download_url || "",
            uuid: fileId!,
          },
          taxAmount: values.taxAmountPayable,
          grossAssetValue: values.grossAssetValue,
          groupId: groupId,
        })
      );
      close();
    }
  };

  const handleDiscardDraftClose = () => {
    dispatch(resetDftDraftForReview());
    discardDraft.close();
    close();
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content size="xxl">
          <FormikForm
            validateOnChange={false}
            validationSchema={DftReviewSchema}
            onSubmit={handleSendDraftToCustomer}
            initialValues={{
              taxAmountPayable,
              grossAssetValue,
            }}
          >
            <>
              <Modal.Header>
                <Modal.Title>Send Filing Draft For Review</Modal.Title>
                <Modal.Close type="button" />
              </Modal.Header>
              <Modal.Body className="!t-p-0">
                <Async.Root
                  isLoading={isLoading || isPreviewLoading}
                  isEmpty={false}
                  isSuccess={isSuccess && isPreviewSuccess}
                  customLoader={<ParseDftLoader />}
                >
                  <Async.Success>
                    <div className="t-flex t-gap-1">
                      <iframe
                        className="t-flex-1 t-h-[500px]"
                        src={fileUrl}
                        title="Document Preview"
                      />
                      <div className="t-w-96 t-px-8 t-py-6">
                        <div className="t-m-0 t-flex t-flex-col t-gap-5">
                          <div className="t-text-body-sm t-text-text-30">
                            Fill in the required details and send draft to the
                            customer for review
                          </div>
                          <div className="t-flex t-flex-col t-gap-4">
                            <div>
                              <PriceInput
                                name="taxAmountPayable"
                                label="Tax payable"
                                required
                                placeholder="0.00"
                              />
                              <ToolTip
                                text={
                                  <span>
                                    Delaware Franchise Tax is calculated using
                                    your authorized shares, issued shares, and
                                    total gross assets.{" "}
                                    <Link
                                      className="!t-text-purple-30"
                                      to="https://www.inkle.ai/free-tools/dft-calculator"
                                    >
                                      Learn more
                                    </Link>
                                  </span>
                                }
                                side="right"
                                propClass="!t-text-left t-w-72"
                              >
                                <span className="t-text-body-sm-lite t-italic t-text-neutral-40 t-w-fit t-flex t-items-center t-gap-0.5">
                                  How is it calculated
                                  <QuestionWithCircle />
                                </span>
                              </ToolTip>
                            </div>
                            <div>
                              <PriceInput
                                name="grossAssetValue"
                                label="Gross asset value"
                                required
                                placeholder="0.00"
                              />
                              <ToolTip
                                text="The gross asset value is calculated by adding up the company’s year-end investments, cash in bank accounts + in hand, fixed assets, crypto, inventory, real estate, loans to subsidiaries, and accounts receivable."
                                side="right"
                                propClass="!t-text-left t-w-72"
                              >
                                <span className="t-text-body-sm-lite t-italic t-text-neutral-40 t-w-fit t-flex t-items-center t-gap-0.5">
                                  How is it calculated?
                                  <QuestionWithCircle />
                                </span>
                              </ToolTip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Async.Success>
                </Async.Root>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button onClick={discardDraft.open} type="button">
                  Cancel
                </Button>
                <Button customType="primary" disabled={isLoading} type="submit">
                  Send to customer
                </Button>
              </Modal.FooterButtonGroup>
            </>
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
      <DiscardFilingDraft
        {...discardDraft}
        onDiscard={handleDiscardDraftClose}
      />
    </>
  );
};
